import domStyle from 'dojo/dom-style';

import sleep from 'src/utils/sleep';
import 'src/web-components/GWorksWindow';
import './gwindow.less';

/**
 * @typedef {object} GWindowConfig
 * @property {boolean} [autoResize]
 * @property {string} [class]
 * @property {(key: string) => void} [closeCallback]
 * @property {HTMLElement} content
 * @property {string} id
 * @property {string} [key]
 * @property {Partial<CSSStyleDeclaration>} [position]
 * @property {boolean} [showCloseButton]
 * @property {boolean} [startMinimized]
 * @property {string} title
 */

class GWindow {
  domNode = document.createElement('gworks-window');

  /**
   * @param {GWindowConfig} config
   */
  constructor (config) {
    this.domNode.classList.add('gisw-wndw');

    Object.assign(this, config);

    const {
      autoResize = false,
      'class': className = '',
      closeCallback = null,
      content = null,
      id,
      key = id,
      position = {},
      showCloseButton = false,
      startMinimized = false,
      title
    } = config;

    /** @private */
    this.callbacks = { close: [] };

    if (closeCallback) {
      this.callbacks.close.push(closeCallback);
    }

    this.id = id;
    this.key = key;
    this.content = content;

    this.domNode.id = id;
    this.domNode.className += ` ${className}`;

    this.domNode.closable = showCloseButton;
    this.domNode.label = title;
    this.domNode.show = !startMinimized;
    this.domNode.appendChild(content);
    this.domNode.addEventListener('close', () => {
      this.callbacks.close.forEach(cb => {
        cb(key);
      });
    });

    if (autoResize) {
      const observer = new MutationObserver(() => {
        this.delayRefresh(550);
      });
      observer.observe(this.domNode, {
        childList: true,
        subtree: true
      });

      this.domNode.addEventListener('close', () => {
        observer.disconnect();
      });
    }

    domStyle.set(this.domNode, Object.assign({ top: '', left: '' }, position));
  }

  destroy () {
    if (this._destroyed) {
      return;
    }

    /** @private */
    this._destroyed = true;

    this.domNode.close();
  }

  get isMinimized () {
    return !this.domNode.show;
  }

  set isMinimized (value) {
    this.domNode.show = !value;
  }

  toggle () {
    this.domNode.toggle();
  }

  close () {
    this.deleteGWin(true);
  }

  /**
   * @param {(key: string) => void} cb
   */
  addCloseCallback (cb) {
    this.callbacks.close.push(cb);
  }

  /**
   * @param {boolean} [closing]
   */
  deleteGWin (closing = false) {
    this.content.remove();

    if (closing) {
      this.callbacks.close.forEach(cb => {
        try {
          cb(this.key);
        } catch (err) {}
      });
    }

    this.destroy();
  }

  async refresh () {
    this.domNode.dispatchEvent(
      new CustomEvent('refresh', {
        bubbles: true,
        detail: { isMinimized: this.isMinimized }
      })
    );

    await sleep();
  }

  /**
   * @param {number} timeout
   */
  delayRefresh (timeout) {
    clearTimeout(this._timeoutID);

    /** @private */
    this._timeoutID = setTimeout(() => {
      if (!this.isMinimized) {
        this.refresh();
      }

      this._timeoutID = null;
    }, timeout);
  }

  /**
   * @param {HTMLElement} node
   */
  setContent (node) {
    this.content.remove();
    this.content = node;
    this.domNode.appendChild(node);
  }
}

export default GWindow;
